import React from "react";
import { motion } from "framer-motion";
import { ArrowDown, Play, Pencil, Apple, GraduationCap, Lightbulb, Notebook, Coffee, Sparkles } from "lucide-react";
import { HERO_PORTRAIT, SHOW_STILLS } from "@/data/products";
import { PixelBurst } from "@/components/PixelBurst";
import { optimizedImg, srcSetFor } from "@/lib/img";

/* Floating brand-colored gradient blob — drifts slowly behind the hero card
   on mobile so the bg has life without distracting from the model. */
function GradientBlob({ color, size = 220, delay = 0, x, y, dur = 12 }) {
  return (
    <motion.div
      aria-hidden
      initial={{ opacity: 0 }}
      animate={{
        opacity: [0.55, 0.75, 0.6, 0.7, 0.55],
        x: [x, x + 18, x - 14, x + 8, x],
        y: [y, y - 12, y + 20, y - 6, y],
      }}
      transition={{ duration: dur, delay, repeat: Infinity, ease: "easeInOut" }}
      className="absolute rounded-full pointer-events-none mix-blend-multiply"
      style={{
        width: size,
        height: size,
        background: color,
        filter: "blur(50px)",
        zIndex: 0,
      }}
    />
  );
}

/* Floating classroom icon — gently drifts + rotates. */
function FloatIcon({ Icon, color, size = 22, x, y, delay = 0, rotateRange = 12, dur = 7 }) {
  return (
    <motion.div
      aria-hidden
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{
        opacity: 1,
        scale: 1,
        y: [y, y - 10, y + 6, y - 4, y],
        rotate: [-rotateRange, rotateRange, -rotateRange / 2, rotateRange / 2, -rotateRange],
      }}
      transition={{
        opacity: { delay: delay + 0.3, duration: 0.5 },
        scale: { delay: delay + 0.3, duration: 0.5 },
        y: { duration: dur, delay, repeat: Infinity, ease: "easeInOut" },
        rotate: { duration: dur * 1.4, delay, repeat: Infinity, ease: "easeInOut" },
      }}
      className="absolute pointer-events-none flex items-center justify-center border-2 border-[#111] shadow-brutal-sm"
      style={{
        left: x,
        top: y,
        width: size + 16,
        height: size + 16,
        background: color,
        zIndex: 1,
      }}
    >
      <Icon size={size} strokeWidth={2.5} className="text-[var(--st-fg)]" />
    </motion.div>
  );
}

/* HERO — single-fold "URBAN/ANGEL" composition with a dramatic on-load reveal.
 *   DESKTOP (>=lg): left-aligned mega title sandwich, CB3 banner anchored on
 *                   the right overlapping the type. Letters stagger in from
 *                   below, CB3 swoops in with a tiny rotation + scan sweep.
 *   MOBILE (<lg):   magazine-cover card with model framed up top and the
 *                   title falling letter-by-letter at the bottom of the card.
 */

const TITLE_EASE = [0.22, 1, 0.36, 1];

/* Animated word — each letter drops down with a stagger.
 * Supports per-letter color via `accent` map (index → CSS color var). */
const RevealWord = ({
  text,
  baseColor,
  accent = {},
  delay = 0,
  className = "",
  testId,
}) => {
  const letters = text.split("");
  return (
    <motion.h1
      data-testid={testId}
      initial="hidden"
      animate="visible"
      variants={{
        hidden: {},
        visible: { transition: { delayChildren: delay, staggerChildren: 0.045 } },
      }}
      className={`font-display uppercase leading-[0.86] tracking-tight select-none whitespace-nowrap ${className}`}
      style={{ color: baseColor }}
    >
      {letters.map((ch, i) => (
        <motion.span
          key={i}
          variants={{
            hidden: { y: "100%", opacity: 0, rotate: -4 },
            visible: { y: 0, opacity: 1, rotate: 0 },
          }}
          transition={{ duration: 0.7, ease: TITLE_EASE }}
          className="inline-block"
          style={accent[i] ? { color: accent[i] } : undefined}
        >
          {ch === " " ? "\u00A0" : ch}
        </motion.span>
      ))}
    </motion.h1>
  );
};

const PixelMark = () => (
  <div className="flex flex-col gap-[2px]" aria-hidden>
    {["■■..■", ".■■.■", "■■■.."].map((row, r) => (
      <div key={r} className="flex gap-[2px]">
        {row.split("").map((c, i) => (
          <motion.span
            key={i}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.05 * (r * 5 + i) + 0.1, duration: 0.2 }}
            className="block w-3 h-3"
            style={{ background: c === "■" ? "var(--st-mustard)" : "transparent" }}
          />
        ))}
      </div>
    ))}
  </div>
);

/* ────────────────────────────────────────────────────────────
 *  Local helper: the loader covers the screen for ~2.1s on every
 *  refresh, so the decorative pixel burst always waits until then.
 * ──────────────────────────────────────────────────────────── */
const useBurstDelay = () => 2.3;

/* ────────────────────────────────────────────────────────────
 *  MOBILE / TABLET — magazine-cover composition
 * ──────────────────────────────────────────────────────────── */
const HeroMobile = () => {
  const burstDelay = useBurstDelay();
  return (
  <div className="lg:hidden relative">
    {/* ─── MOBILE-ONLY animated brand backdrop ─── */}
    <div aria-hidden className="absolute inset-x-[-12%] top-8 bottom-0 overflow-hidden pointer-events-none -z-10">
      <GradientBlob color="var(--st-maroon)" size={260} x={-40} y={120} dur={14} delay={0} />
      <GradientBlob color="var(--st-mustard)" size={300} x={170} y={40} dur={16} delay={0.6} />
      <GradientBlob color="var(--st-gold)" size={220} x={-20} y={420} dur={12} delay={1.2} />
      <GradientBlob color="var(--st-orange)" size={200} x={210} y={520} dur={15} delay={1.5} />
    </div>

    {/* ─── MOBILE-ONLY floating classroom icons — corner stickers and
            margin floaters layered ABOVE the hero card. ─── */}
    <div aria-hidden className="absolute inset-0 pointer-events-none z-20 overflow-hidden">
      {/* corner stickers attached to the card */}
      <FloatIcon Icon={Pencil} color="var(--st-mustard)" x="-3%" y={110} delay={0.4} dur={6.2} size={22} rotateRange={15} />
      <FloatIcon Icon={Sparkles} color="var(--st-orange)" x="86%" y={88} delay={0.2} dur={5.5} size={20} rotateRange={20} />
      <FloatIcon Icon={GraduationCap} color="var(--st-cream)" x="-4%" y={380} delay={1.1} dur={8} size={22} rotateRange={10} />
      <FloatIcon Icon={Apple} color="var(--st-gold)" x="88%" y={350} delay={0.8} dur={7.5} size={20} rotateRange={12} />
      <FloatIcon Icon={Notebook} color="var(--st-mustard)" x="-2%" y={555} delay={1.4} dur={6.8} size={20} rotateRange={10} />
      <FloatIcon Icon={Lightbulb} color="var(--st-orange)" x="84%" y={580} delay={1.7} dur={7.2} size={20} rotateRange={14} />
      <FloatIcon Icon={Coffee} color="var(--st-cream)" x="42%" y={-4} delay={0.6} dur={5.8} size={18} rotateRange={18} />
    </div>

    {/* tiny chip row */}
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1, duration: 0.4 }}
      className="relative z-10 flex items-center justify-between mb-4"
    >
      <div className="inline-flex items-center gap-2 border-2 border-[#111] bg-[var(--st-cream)] px-2.5 py-1 shadow-brutal-sm">
        <span className="w-2 h-2 bg-[var(--st-maroon)] animate-pulse rounded-full" />
        <span className="font-heading uppercase tracking-[0.2em] text-[10px]">
          Tubi TV · Drop 01
        </span>
      </div>
      <a
        href="#newsletter"
        data-testid="hero-signup-cta"
        className="inline-flex items-center gap-1 border-2 border-[#111] bg-[var(--st-mustard)] hover:bg-[var(--st-gold)] px-2.5 py-1 font-heading uppercase tracking-[0.18em] text-[10px] shadow-brutal-sm transition-colors"
      >
        Sign Up <span aria-hidden>↗</span>
      </a>
    </motion.div>

    {/* HERO CARD — model framed, title overlaid at bottom */}
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: 0.2, duration: 0.75, ease: TITLE_EASE }}
      className="relative z-10 border-2 border-[#111] bg-[var(--st-cream)] shadow-brutal overflow-hidden"
    >
      {/* pixel burst from top-right corner of card */}
      <PixelBurst origin="top-right" count={16} baseDelay={burstDelay} />
      <div className="absolute top-3 left-3 z-20 bg-[var(--st-maroon)] text-white px-2 py-0.5 font-heading uppercase tracking-[0.18em] text-[10px]">
        Ep. 01 · CB3
      </div>
      <div className="absolute top-3 right-3 z-20 bg-[var(--st-fg)] text-[var(--st-gold)] px-2 py-0.5 font-pixel text-[12px]">
        ● LIVE
      </div>

      {/* model image — fills upper portion + scan sweep */}
      <div className="relative aspect-[4/5] overflow-hidden bg-[var(--st-cream)]">
        <motion.img
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.4, duration: 1.1, ease: TITLE_EASE }}
          src={optimizedImg(HERO_PORTRAIT, 1200)}
          srcSet={srcSetFor(HERO_PORTRAIT, [640, 960, 1200, 1600])}
          sizes="(max-width: 768px) 100vw, 60vw"
          alt="Charles Brockman III — The Know It Owls"
          className="absolute inset-0 w-full h-full object-cover object-top drop-shadow-[6px_8px_0_rgba(0,0,0,0.18)]"
          draggable={false}
          loading="eager"
          fetchpriority="high"
          decoding="async"
        />

        {/* scan sweep */}
        <motion.div
          aria-hidden
          initial={{ x: "-110%" }}
          animate={{ x: "110%" }}
          transition={{ delay: 0.55, duration: 0.9, ease: "easeInOut" }}
          className="absolute inset-y-0 w-1/2 z-[5] pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(232,163,23,0.0) 30%, rgba(232,163,23,0.55) 50%, rgba(232,163,23,0.0) 70%, transparent 100%)",
            mixBlendMode: "screen",
          }}
        />

        {/* SUBSTITUTE + TEACHER stacked tight at the bottom of the card.
            Sits OVER the yellow hoodie, so accent letters are WHITE (not
            mustard/gold which vanish into the hoodie). Hard maroon
            drop-shadow gives every letter a sharp outline so it stays
            readable on both cream background AND yellow garment. */}
        <div className="absolute bottom-3 left-0 right-0 z-10 px-2 select-none">
          <RevealWord
            text="SUBSTITUTE"
            baseColor="#ffffff"
            accent={{
              0: "#ffffff",
              1: "#ffffff",
              2: "#ffffff",
            }}
            delay={0.6}
            className="text-[14vw] sm:text-[12vw] text-center whitespace-nowrap [text-shadow:3px_3px_0_var(--st-maroon),-2px_-2px_0_var(--st-maroon),2px_-2px_0_var(--st-maroon),-2px_2px_0_var(--st-maroon),0_2px_0_var(--st-maroon),2px_0_0_var(--st-maroon),0_-2px_0_var(--st-maroon),-2px_0_0_var(--st-maroon)]"
            testId="hero-title-line-1"
          />
          <RevealWord
            text="TEACHER"
            baseColor="#ffffff"
            accent={{ 1: "#ffffff", 2: "#ffffff" }}
            delay={0.95}
            className="text-[14vw] sm:text-[12vw] text-center whitespace-nowrap [text-shadow:3px_3px_0_var(--st-maroon),-2px_-2px_0_var(--st-maroon),2px_-2px_0_var(--st-maroon),-2px_2px_0_var(--st-maroon),0_2px_0_var(--st-maroon),2px_0_0_var(--st-maroon),0_-2px_0_var(--st-maroon),-2px_0_0_var(--st-maroon)] -mt-1"
            testId="hero-title-line-2"
          />
        </div>
      </div>
    </motion.div>

    {/* below-card row */}
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.3 }}
      className="mt-4 grid grid-cols-12 gap-3 items-center"
    >
      <div className="col-span-7">
        <p className="font-body text-sm leading-snug text-[var(--st-fg)]">
          Exclusive streetwear from the show. Class is in session.
        </p>
      </div>
      <a
        href="#shop"
        data-testid="hero-shop-cta"
        className="col-span-5 inline-flex items-center justify-center gap-1.5 border-2 border-[#111] bg-[var(--st-maroon)] text-white hover:bg-[var(--st-gold)] hover:text-[var(--st-fg)] px-3 py-2.5 font-heading uppercase tracking-[0.18em] text-[11px] shadow-brutal-sm transition-colors"
      >
        Shop Drop 01 <span aria-hidden>↗</span>
      </a>
    </motion.div>
  </div>
  );
};

/* ────────────────────────────────────────────────────────────
 *  DESKTOP — single fold, CB3 as right-anchor, dramatic reveal
 * ──────────────────────────────────────────────────────────── */
const HeroDesktop = () => {
  const burstDelay = useBurstDelay();
  return (
  <div className="hidden lg:block relative">
    {/* ─── DESKTOP/iPad animated brand backdrop ─── */}
    <div aria-hidden className="absolute inset-x-[-4%] top-0 bottom-0 overflow-hidden pointer-events-none -z-10">
      <GradientBlob color="var(--st-maroon)" size={420} x={-100} y={120} dur={16} delay={0} />
      <GradientBlob color="var(--st-mustard)" size={520} x={320} y={-40} dur={18} delay={0.6} />
      <GradientBlob color="var(--st-gold)" size={380} x={-60} y={480} dur={14} delay={1.2} />
      <GradientBlob color="var(--st-orange)" size={420} x={620} y={520} dur={17} delay={1.5} />
      <GradientBlob color="var(--st-maroon)" size={360} x={920} y={140} dur={15} delay={0.9} />
    </div>

    {/* ─── DESKTOP/iPad floating classroom-icon stickers ─── */}
    <div aria-hidden className="absolute inset-0 pointer-events-none z-20 overflow-hidden">
      <FloatIcon Icon={Coffee} color="var(--st-cream)" x="6%" y={-10} delay={0.6} dur={6.5} size={22} rotateRange={16} />
      <FloatIcon Icon={Pencil} color="var(--st-mustard)" x="-1%" y={150} delay={0.4} dur={6.8} size={26} rotateRange={18} />
      <FloatIcon Icon={GraduationCap} color="var(--st-cream)" x="2%" y={380} delay={1.1} dur={8} size={26} rotateRange={12} />
      <FloatIcon Icon={Notebook} color="var(--st-mustard)" x="-1%" y={580} delay={1.4} dur={7.2} size={24} rotateRange={10} />
      <FloatIcon Icon={Apple} color="var(--st-orange)" x="48%" y={-12} delay={0.2} dur={5.8} size={22} rotateRange={20} />
      <FloatIcon Icon={Sparkles} color="var(--st-orange)" x="96%" y={120} delay={0.5} dur={5.5} size={24} rotateRange={22} />
      <FloatIcon Icon={Lightbulb} color="var(--st-mustard)" x="98%" y={320} delay={1.7} dur={7.4} size={26} rotateRange={16} />
      <FloatIcon Icon={Coffee} color="var(--st-gold)" x="94%" y={540} delay={0.9} dur={6.2} size={24} rotateRange={14} />
    </div>

    {/* top utility row */}
    <div className="flex items-start justify-between mb-2 relative z-30">
      <motion.div
        initial={{ opacity: 0, x: -16 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
      >
        <PixelMark />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.12 }}
        className="inline-flex items-center gap-2 border-2 border-[#111] bg-[var(--st-cream)] px-3 py-1.5 shadow-brutal-sm"
      >
        <span className="w-2 h-2 bg-[var(--st-maroon)] animate-pulse rounded-full" />
        <span className="font-heading uppercase tracking-[0.2em] text-xs">
          Coming to Tubi TV · Drop 01 · Class is in session
        </span>
      </motion.div>

      <motion.a
        href="#newsletter"
        initial={{ opacity: 0, x: 16 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.15 }}
        data-testid="hero-signup-cta-desk"
        className="inline-flex items-center gap-1.5 border-2 border-[#111] bg-[var(--st-mustard)] hover:bg-[var(--st-gold)] px-3 py-1.5 font-heading uppercase tracking-[0.18em] text-xs shadow-brutal-sm transition-colors"
      >
        Sign Up <span aria-hidden>↗</span>
      </motion.a>
    </div>

    {/* MAIN STAGE — title block + model overlap */}
    <div className="relative min-h-[50vh] xl:min-h-[52vh] overflow-hidden">
      {/* Decorative pixel burst — sprinkles from top-right corner on load */}
      <PixelBurst origin="top-right" count={42} baseDelay={burstDelay} />
      {/* Title block — left aligned, two lines stacked with proper spacing */}
      <div className="relative z-10 max-w-[78%] xl:max-w-[80%]">
        <div className="overflow-hidden pb-[2vh]">
          <RevealWord
            text="SUBSTITUTE"
            baseColor="var(--st-maroon)"
            accent={{
              0: "var(--st-mustard)",
              1: "var(--st-mustard)",
              2: "var(--st-mustard)",
            }}
            delay={0.25}
            className="text-left text-[10.5vw] xl:text-[10vw] 2xl:text-[9.5vw]"
            testId="hero-title-line-1-desk"
          />
        </div>
        <div className="overflow-hidden mt-1">
          <RevealWord
            text="TEACHER"
            baseColor="var(--st-maroon)"
            accent={{ 1: "var(--st-gold)", 2: "var(--st-gold)" }}
            delay={0.7}
            className="text-left text-[10.5vw] xl:text-[10vw] 2xl:text-[9.5vw]"
            testId="hero-title-line-2-desk"
          />
        </div>
      </div>

      {/* MODEL — anchored to the bottom of the stage. Height is clamped to the
          stage height so the whole image is ALWAYS visible (never cut). Width
          is auto so the aspect ratio is preserved. Same treatment across all
          desktop breakpoints. */}
      <motion.div
        initial={{ opacity: 0, x: 60, y: 30, scale: 0.92, rotate: 4 }}
        animate={{ opacity: 1, x: 0, y: 0, scale: 1, rotate: 0 }}
        transition={{ delay: 0.45, duration: 1.0, ease: TITLE_EASE }}
        className="absolute z-20 pointer-events-none"
        style={{
          bottom: 0,
          right: "-2%",
          height: "100%",
          width: "auto",
        }}
      >
        <div className="relative h-full">
          <img
            src={optimizedImg(HERO_PORTRAIT, 900)}
            srcSet={srcSetFor(HERO_PORTRAIT, [640, 960, 1200, 1600])}
            sizes="(max-width: 768px) 100vw, 60vw"
            alt="Charles Brockman III — The Know It Owls"
            className="h-full w-auto object-contain object-bottom select-none drop-shadow-[10px_12px_0_rgba(0,0,0,0.15)]"
            draggable={false}
            loading="eager"
            fetchpriority="high"
            decoding="async"
          />
          {/* scan sweep */}
          <motion.div
            aria-hidden
            initial={{ x: "-120%", opacity: 0 }}
            animate={{ x: "120%", opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.95, ease: "easeInOut" }}
            className="absolute inset-y-0 w-2/3 pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.0) 30%, rgba(255,255,255,0.55) 50%, rgba(232,163,23,0.0) 70%, transparent 100%)",
              mixBlendMode: "screen",
            }}
          />
        </div>
      </motion.div>

      {/* BOTTOM utility row */}
      <div className="absolute left-0 right-0 bottom-2 flex items-end justify-between gap-4 z-30">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.1 }}
          className="w-[280px]"
        >
          <div className="flex items-center gap-3 font-pixel text-base mb-2">
            <span className="text-[var(--st-maroon)] font-bold border-b-2 border-[var(--st-maroon)] pb-0.5">
              01
            </span>
            <span className="text-[var(--st-fg)]/25">02</span>
            <span className="text-[var(--st-fg)]/25">03</span>
            <span className="text-[var(--st-fg)]/25">04</span>
            <span className="text-[var(--st-fg)]/25">05</span>
          </div>
          <p className="font-body text-sm leading-snug text-[var(--st-fg)]">
            Exclusive streetwear from the show. Built like the syllabus — sharp, soulful, made to
            last.
          </p>
        </motion.div>

        <motion.a
          href="#shop"
          aria-label="Scroll to shop"
          initial={{ opacity: 0, y: 10, rotate: 0 }}
          animate={{ opacity: 1, y: 0, rotate: 45 }}
          transition={{ delay: 1.35, duration: 0.55 }}
          className="hidden xl:flex w-12 h-12 bg-[var(--st-mustard)] border-2 border-[#111] items-center justify-center hover:bg-[var(--st-gold)] transition-colors shadow-brutal-sm shrink-0 mb-1"
        >
          <ArrowDown size={18} className="-rotate-45 text-[var(--st-fg)]" strokeWidth={3} />
        </motion.a>

        <motion.a
          href="#class-in-action"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.2 }}
          className="flex items-end gap-3 group"
        >
          <span className="hidden xl:inline font-heading uppercase tracking-[0.22em] text-xs text-[var(--st-fg)]/70 group-hover:text-[var(--st-maroon)] transition-colors pb-2">
            Watch Sneak Peek
          </span>
          <div className="relative w-[140px] aspect-[4/3] border-2 border-[#111] overflow-hidden shadow-brutal-sm">
            <img
              src={optimizedImg(SHOW_STILLS[0], 280)}
              alt="show still"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover grayscale transition-all duration-500 group-hover:grayscale-0"
            />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-10 h-10 bg-white/95 border-2 border-[#111] flex items-center justify-center">
                <Play size={14} fill="currentColor" className="text-[var(--st-maroon)]" />
              </span>
            </span>
          </div>
        </motion.a>
      </div>
    </div>
  </div>
  );
};

export default function Hero() {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative grid-paper border-b-2 border-[#111] overflow-hidden"
    >
      <div className="max-w-[1500px] mx-auto px-4 md:px-8 pt-4 md:pt-6 pb-6 lg:pb-4 relative">
        <HeroMobile />
        <HeroDesktop />
      </div>
    </section>
  );
}
